/**
 * Spillway (§7.1, view 4): gate elevation view, per-gate discharge, imbalance indicator, the
 * sequencing assistant, live and projected TDG, heaters and hoist status.
 */

import { useState } from 'react';
import { useSession } from '../../store/session';
import { useDispatch } from '../useEngine';
import { getSite } from '../../sites';
import { gateDischarge, openingForDischarge } from '../../engine/gates';
import { makeTdgModel } from '../../engine/tdg';
import { Trend } from '../charts/Trend';
import { Readout } from '../widgets/Readout';
import { JogButtons } from '../widgets/Jog';
import { useConfirmed } from '../widgets/Confirm';
import { Gloss } from '../glossary/GlossaryDrawer';

export function SpillwayView(): JSX.Element {
  const snap = useSession((s) => s.snapshot);
  const site = getSite(useSession((s) => s.siteId));
  const selected = useSession((s) => s.selectedGate);
  const selectGate = useSession((s) => s.selectGate);
  const dispatch = useDispatch();
  const confirmed = useConfirmed();
  const [want, setWant] = useState(1000);

  if (!snap) return <div style={{ padding: 24 }}>—</div>;
  if (site.spillway.kind !== 'gated') {
    return (
      <div style={{ padding: 'var(--s5)' }}>
        <div className="title">Uncontrolled spillway</div>
        <p style={{ color: 'var(--ink-dim)', maxWidth: 560 }}>
          {site.name} has a free-crest spillway at {site.spillway.sill.toFixed(1)} m. There is
          nothing to operate — the only levers are generation and the low-level outlet.
        </p>
      </div>
    );
  }

  const sp = site.spillway;
  const tdg = makeTdgModel(site);
  const imbalance = snap.gates.reduce(
    (worst, g, i) => (i === 0 ? 0 : Math.max(worst, Math.abs(g.opening - (snap.gates[i - 1]?.opening ?? 0)))),
    0,
  );

  // Sequencing assistant: equal openings across the healthy gates, in the recommended order.
  const healthy = snap.gates.filter((g) => g.hoist === 'ok' && !g.damaged);
  const perGate = healthy.length > 0 ? want / healthy.length : 0;
  const suggested = healthy.length > 0 ? openingForDischarge(site, snap.headwater, perGate) : 0;
  const capacity = snap.gates.reduce(
    (a, g) => a + (g.damaged ? Math.min(sp.damagedGateFlowCap, gateDischarge(site, snap.headwater, sp.gateHeight)) : gateDischarge(site, snap.headwater, sp.gateHeight)),
    0,
  );
  const travelMinutes = Math.abs(suggested - (snap.gates[0]?.opening ?? 0)) / sp.hoistRate;
  const projectedTdg = tdg.mix([
    ...healthy.map(() => ({ q: perGate, tdg: tdg.spillTdg(perGate, sp.gateWidth) })),
    { q: snap.turbineFlow, tdg: site.powerhouse.tdgPercent },
    { q: snap.lloFlow, tdg: site.llo.tdgPercent },
  ]);

  return (
    <div style={{ padding: 'var(--s3)', display: 'grid', gap: 'var(--s3)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 'var(--s2)' }}>
        <Readout label="Total spill" value={Math.round(snap.spillFlow).toLocaleString('en-US')} unit="m³/s" />
        <Readout
          label="Capacity at this pool"
          value={Math.round(capacity).toLocaleString('en-US')}
          unit="m³/s"
          size="sm"
          hint={`Sill is at ${sp.sill} m — the spillway passes nothing below it, and little until well above it.`}
        />
        <Readout
          label="Adjacent imbalance"
          value={imbalance.toFixed(2)}
          unit="m"
          size="sm"
          tone={imbalance > sp.adjacencyLimit ? 'alarm' : imbalance > sp.adjacencyLimit * 0.7 ? 'warn' : 'ok'}
          hint={`Limit ${sp.adjacencyLimit.toFixed(1)} m. Asymmetric flow loads the stilling basin.`}
        />
        <Readout
          label="TDG tailrace"
          value={snap.tdgTailrace.toFixed(1)}
          unit="%"
          size="sm"
          tone={snap.tdgTailrace > snap.tdgLimit ? 'alarm' : 'normal'}
          hint={`Limit ${snap.tdgLimit}%`}
        />
        <Readout
          label="Waiver"
          value={snap.tdgWaiverGranted ? 'granted' : snap.tdgWaiverRequested ? 'requested' : 'none'}
          size="sm"
          tone={snap.tdgWaiverGranted ? 'ok' : snap.tdgWaiverRequested ? 'warn' : 'dim'}
        />
      </div>

      <section className="panel">
        <div className="label" style={{ marginBottom: 8 }}>
          Gate elevation view — leaves drawn at their actual position
        </div>
        <GateElevation site={site} snap={snap} selected={selected} onSelect={selectGate} />
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(340px,1fr))', gap: 'var(--s3)' }}>
        <section className="panel">
          <div className="label" style={{ marginBottom: 8 }}>
            Sequencing assistant
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 10 }}>
            <label style={{ flex: 1 }}>
              <div className="label">Target spill (m³/s)</div>
              <input type="number" className="num" value={want} onChange={(e) => setWant(Number(e.target.value) || 0)} />
            </label>
            <button
              className="primary"
              disabled={healthy.length === 0 || capacity < 1}
              onClick={() =>
                confirmed(
                  `Set ${healthy.length} gates to ${suggested.toFixed(2)} m`,
                  `Passes about ${Math.round(want).toLocaleString('en-US')} m³/s. Travel takes roughly ${Math.round(travelMinutes)} minutes at ${sp.hoistRate} m/min.`,
                  () => {
                    for (const g of snap.gates) {
                      if (g.hoist === 'ok' && !g.damaged) dispatch({ t: 'gate-target', gate: g.id, opening: suggested });
                    }
                  },
                )
              }
            >
              Apply
            </button>
          </div>
          {capacity < 1 ? (
            <div style={{ fontSize: 13, color: 'var(--warn)' }}>
              ▲ The pool is at <span className="num">{snap.headwater.toFixed(2)} m</span> and the
              spillway sill is at <span className="num">{sp.sill.toFixed(1)} m</span>. Opening the
              gates passes <strong>nothing</strong> — the spillway is dry.
              <div style={{ color: 'var(--ink-dim)', marginTop: 6 }}>
                This is the counter-intuitive part of drawing a reservoir down: every metre you
                drop takes spillway capacity away from you, and below the sill you have none at
                all. At this pool your only release paths are the machines and the low-level
                outlets.
              </div>
            </div>
          ) : want > capacity ? (
            <div style={{ fontSize: 13, color: 'var(--warn)' }}>
              ▲ At <span className="num">{snap.headwater.toFixed(2)} m</span> the spillway can pass
              at most <span className="num">{Math.round(capacity).toLocaleString('en-US')} m³/s</span>{' '}
              with every healthy gate at full travel — less than the{' '}
              <span className="num">{Math.round(want).toLocaleString('en-US')} m³/s</span> requested.
              <div style={{ color: 'var(--ink-dim)', marginTop: 6 }}>
                Applying this opens every gate fully. The remainder has to come from the machines,
                the low-level outlets, or storage.
              </div>
            </div>
          ) : healthy.length > 0 ? (
            <div style={{ fontSize: 13 }}>
              To spill <span className="num">{Math.round(want).toLocaleString('en-US')} m³/s</span> from
              the current openings:{' '}
              <strong>
                {sp.sequence
                  .filter((i) => healthy.some((g) => g.id === i))
                  .map((i) => `Gate ${i + 1} to ${suggested.toFixed(2)} m`)
                  .join(', ')}
              </strong>
              .
              <div style={{ color: 'var(--ink-dim)', marginTop: 6 }} className="num">
                About {Math.round(travelMinutes)} minutes of travel. Projected tailrace TDG{' '}
                <span style={{ color: projectedTdg > snap.tdgLimit ? 'var(--alarm)' : 'var(--ok)' }}>
                  {projectedTdg.toFixed(1)}%
                </span>{' '}
                against a limit of {snap.tdgLimit}%.
              </div>
              <div style={{ color: 'var(--ink-dim)', marginTop: 6 }}>
                Recommended order is centre-out ({sp.sequence.map((i) => i + 1).join(', ')}) for
                symmetric flow into the stilling basin. Keep adjacent gates within{' '}
                {sp.adjacencyLimit.toFixed(1)} m of each other, and never park a gate between 0.01 m
                and {sp.minNonVibrating.toFixed(2)} m.
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--alarm)' }}>No healthy gates available.</div>
          )}
        </section>

        <section className="panel">
          <div className="label" style={{ marginBottom: 6 }}>
            <Gloss id="tdg">Total dissolved gas</Gloss> — tailrace and gauge
          </div>
          <Trend
            height={160}
            yLabel="%"
            fmt={(v) => v.toFixed(0)}
            series={[{ x: snap.history.minuteT, y: snap.history.tdg, colour: 'var(--water-shallow)', width: 1.8, label: 'tailrace' }]}
            thresholds={[
              { value: site.licence.tdgLimit, colour: 'var(--warn)', label: `licence ${site.licence.tdgLimit}%` },
              { value: site.licence.tdgFloodLimit, colour: 'var(--ok)', label: `waiver ${site.licence.tdgFloodLimit}%` },
            ]}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <button disabled={snap.tdgWaiverRequested} onClick={() => dispatch({ t: 'request-tdg-waiver' })}>
              Request TDG waiver
            </button>
            <button
              className={snap.floodOpsDeclared ? 'armed' : undefined}
              onClick={() => dispatch({ t: 'declare-flood-ops', declared: !snap.floodOpsDeclared })}
            >
              {snap.floodOpsDeclared ? 'Flood ops declared' : 'Declare flood operations'}
            </button>
          </div>
          <div style={{ color: 'var(--ink-dim)', fontSize: 12, marginTop: 6 }}>
            Gas production goes with discharge per metre of gate width, not with total spill.
            Spreading the same flow across more gates is the single most effective thing you can do.
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="label" style={{ marginBottom: 8 }}>
          Per-gate detail
        </div>
        <div className="table-scroll">
        <table style={{ width: '100%', minWidth: 640, fontSize: 12 }} className="num">
          <thead>
            <tr style={{ color: 'var(--ink-dim)', textAlign: 'left' }}>
              <th>Gate</th>
              <th>Opening</th>
              <th>Target</th>
              <th>Discharge</th>
              <th>q per m</th>
              <th>TDG</th>
              <th>Hoist</th>
              <th>Heater</th>
              <th>Jog</th>
            </tr>
          </thead>
          <tbody>
            {snap.gates.map((g, i) => (
              <tr
                key={g.id}
                onClick={() => selectGate(i)}
                style={{
                  background: selected === i ? 'var(--surface-2)' : undefined,
                  cursor: 'pointer',
                }}
              >
                <td>{i + 1}</td>
                <td>{g.opening.toFixed(2)} m</td>
                <td style={{ color: Math.abs(g.target - g.opening) > 0.005 ? 'var(--water-moving)' : 'var(--ink-dim)' }}>
                  {g.target.toFixed(2)} m
                </td>
                <td>{Math.round(g.q).toLocaleString('en-US')}</td>
                <td style={{ color: g.q / sp.gateWidth > 14.3 ? 'var(--warn)' : 'var(--ink-dim)' }}>
                  {(g.q / sp.gateWidth).toFixed(1)}
                </td>
                <td style={{ color: g.tdg > snap.tdgLimit ? 'var(--alarm)' : 'var(--ink-dim)' }}>{g.tdg.toFixed(0)}%</td>
                <td style={{ color: g.hoist === 'ok' ? 'var(--ink-dim)' : 'var(--alarm)' }}>
                  {g.hoist === 'ok' ? (g.damaged ? 'DAMAGED' : 'ok') : g.hoist.toUpperCase()}
                </td>
                <td>
                  <button
                    style={{ padding: '1px 6px' }}
                    className={g.heaterOn ? 'armed' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch({ t: 'gate-heater', gate: i, on: !g.heaterOn });
                    }}
                  >
                    {g.heaterOn ? `${Math.round(g.heaterMinutes)}m` : 'off'}
                  </button>
                </td>
                <td>
                  <JogButtons
                    label={`gate ${i + 1}`}
                    step={0.25}
                    ariaLabel={`Gate ${i + 1} jog`}
                    disabled={g.hoist !== 'ok'}
                    onJog={(d) =>
                      dispatch({
                        t: 'gate-target',
                        gate: i,
                        opening: Math.max(0, Math.min(sp.gateHeight, g.target + d)),
                      })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </div>
  );
}

function GateElevation({
  site,
  snap,
  selected,
  onSelect,
}: {
  site: ReturnType<typeof getSite>;
  snap: NonNullable<ReturnType<typeof useSession.getState>['snapshot']>;
  selected: number;
  onSelect(i: number): void;
}): JSX.Element {
  const sp = site.spillway;
  const top = sp.sill + sp.gateHeight + 4;
  const bottom = sp.sill - 6;
  const H = 200;
  const y = (e: number): number => 10 + ((top - e) / (top - bottom)) * (H - 26);
  const n = snap.gates.length;
  const w = 900;
  const gw = (w - 40) / n;

  return (
    <svg viewBox={`0 0 ${w} ${H}`} width="100%" height={H} role="img" aria-label="Spillway gate elevation view">
      {/* Water surface across the whole bay */}
      <rect x={0} y={y(snap.headwater)} width={w} height={Math.max(0, y(bottom) - y(snap.headwater))} fill="var(--water)" opacity={0.35} />
      <line x1={0} x2={w} y1={y(snap.headwater)} y2={y(snap.headwater)} stroke="var(--water-shallow)" strokeWidth={1.5} />
      <text x={4} y={y(snap.headwater) - 4} fontSize={10} fill="var(--water-shallow)" className="num">
        HW {snap.headwater.toFixed(2)} m
      </text>
      <line x1={0} x2={w} y1={y(sp.sill)} y2={y(sp.sill)} stroke="var(--ink-dim)" strokeDasharray="3 3" />
      <text x={4} y={y(sp.sill) + 12} fontSize={10} fill="var(--ink-dim)" className="num">
        sill {sp.sill.toFixed(1)} m
      </text>

      {snap.gates.map((g, i) => {
        const x = 20 + i * gw;
        const leafBottom = sp.sill + g.opening;
        return (
          <g key={g.id} onClick={() => onSelect(i)} style={{ cursor: 'pointer' }}>
            <rect
              x={x + 2}
              y={y(leafBottom + sp.gateHeight)}
              width={gw - 6}
              height={Math.max(3, y(leafBottom) - y(leafBottom + sp.gateHeight))}
              fill="var(--surface-2)"
              stroke={g.hoist !== 'ok' ? 'var(--alarm)' : selected === i ? 'var(--ink)' : 'var(--line)'}
              strokeWidth={selected === i ? 2 : 1}
            />
            {/* Target position ghost */}
            {Math.abs(g.target - g.opening) > 0.005 && (
              <rect
                x={x + 2}
                y={y(sp.sill + g.target + sp.gateHeight)}
                width={gw - 6}
                height={Math.max(3, y(sp.sill + g.target) - y(sp.sill + g.target + sp.gateHeight))}
                fill="none"
                stroke="var(--water-moving)"
                strokeDasharray="3 3"
              />
            )}
            <text x={x + gw / 2} y={H - 6} fontSize={10} fill="var(--ink-dim)" textAnchor="middle" className="num">
              {i + 1}: {g.opening.toFixed(2)} m
            </text>
          </g>
        );
      })}
    </svg>
  );
}
